import { useState, useEffect } from "react";
import { BASE_COLORS } from "config/theme/constants";
import DynamicTable from "components/common/DynamicTable";
import TableHeader from "components/common/TableHeader";
import { ltlCustomersString, stringConfig } from "config/site.string";
import { Breadcrumb, Col, Divider, Drawer, Form, Tooltip } from "antd";
import {
  ActivityLogWrapper,
  ActivityWrapper,
  InfoWrapper,
} from "pages/protectedPages/ActivityLog/styles";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "redux/store";
import { fetchLTLCustomerListRequest } from "redux/ltlCustomers/actions";
import { config } from "config/site.config";
import { isMobile } from "react-device-detect";
import {
  EditOutlined,
  HomeOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import EditLtlCustomers from "./EditLtlCustomers";
import { useNavigate } from "react-router-dom";

export const TABLE_PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_PAGE_SIZE: 10,
  DEFAULT_SORT_FIELD: "wtCode",
  DEFAULT_SORT_ORDER: "DESC",
};

const LtlCustomers = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isInitialized, setIsInitialized] = useState(false);
  //   const permissionsOfUser = loadState("actualPermissionsList");
  //   const { hasViewAccess, hasEditAccess, hasFullAccess }: any =
  //     getGivenPermissions(permissionsOfUser, "LtlCustomers");
  const { ltlCustomerList, loading, totalCount } = useSelector(
    (state: RootState) => state.ltlCustomer
  );
  const [selectedRecord, setSelectedRecord] = useState<any>(null);

  const [pagination, setPagination] = useState({
    current: TABLE_PAGINATION.DEFAULT_PAGE,
    pageSize: TABLE_PAGINATION.DEFAULT_PAGE_SIZE,
    total: 0,
  });
  const [searchValue, setSearchValue] = useState("");
  const [sortField, setSortField] = useState(
    TABLE_PAGINATION.DEFAULT_SORT_FIELD
  );
  const [sortOrder, setSortOrder] = useState(
    TABLE_PAGINATION.DEFAULT_SORT_ORDER
  );
  const [ltlCustomerListData, setLtlCustomerListData] =
    useState(ltlCustomerList);
  const [initialLtlCustomerList, setInitialLtlCustomerList] =
    useState(ltlCustomerList);
  const [isModalOpen, setIsModalOpen] = useState<any>({
    open: false,
    mode: "",
  });
  const [form] = Form.useForm();

  useEffect(() => {
    setLtlCustomerListData(ltlCustomerList);
    setInitialLtlCustomerList(ltlCustomerList);
  }, [ltlCustomerList]);

  useEffect(() => {
    setPagination((prev: any) => ({
      ...prev,
      total: totalCount,
    }));
  }, [totalCount]);

  const handleTableChange = (
    newPagination: any,
    _filters: any,
    sorter: any
  ) => {
    const { current, pageSize } = newPagination;
    const newSortField = sorter.field || TABLE_PAGINATION.DEFAULT_SORT_FIELD;
    const newSortOrder = sorter.order || TABLE_PAGINATION.DEFAULT_SORT_ORDER;

    setPagination((prev) => ({
      ...prev,
      current,
      pageSize,
    }));
    setSortField(newSortField);
    setSortOrder(newSortOrder);

    dispatch(
      fetchLTLCustomerListRequest(config.GetCustomersList, {
        pageNumber: current,
        pageSize,
        sortColumn: newSortField,
        sortOrder: newSortOrder,
        searchValue,
      })
    );
  };

  const handleSearch = (value: string) => {
    dispatch(
      fetchLTLCustomerListRequest(config.GetCustomersList, {
        pageNumber: TABLE_PAGINATION.DEFAULT_PAGE,
        pageSize: pagination.pageSize,
        sortColumn: sortField,
        sortOrder,
        searchValue: value,
      })
    );
    setPagination((prev) => ({
      ...prev,
      current: TABLE_PAGINATION.DEFAULT_PAGE,
    }));
  };

  useEffect(() => {
    setIsInitialized(true);
  }, []);

  useEffect(() => {
    if (isInitialized) {
      dispatch(
        fetchLTLCustomerListRequest(config.GetCustomersList, {
          pageNumber: pagination.current,
          pageSize: pagination.pageSize,
          sortColumn: sortField,
          sortOrder,
          searchValue,
        })
      );
    }
  }, [isInitialized]);

  const handleEdit = (record: any) => {
    setSelectedRecord(record);
    setIsModalOpen({
      open: true,
      mode: "EDIT",
    });
    form.setFieldsValue(record);
  };

  const openAddUser = (from?: string) => {
    setIsModalOpen({
      open: true,
      mode: from,
    });
  };

  const handleOk = () => {
    setIsModalOpen({
      open: false,
      mode: "",
    });
  };

  const handleCancel = () => {
    setIsModalOpen({
      open: false,
      mode: "",
    });
  };

  const columnsData = [
    {
      title: ltlCustomersString.companyName,
      dataIndex: "companyName",
      isSorter: true,
      width: "10%",
    },
    {
      title: ltlCustomersString.wtCode,
      dataIndex: "wtCode",
      isSorter: true,
      width: "7%",
    },
    {
      title: ltlCustomersString.email,
      dataIndex: "email",
      isSorter: true,
      width: "10%",
    },
    {
      title: ltlCustomersString.phone,
      dataIndex: "phone",
      isSorter: true,
      width: "10%",
      render: (phone: any, record: { phoneExt: any }) => {
        return record.phoneExt && phone
          ? `+${record.phoneExt} ${phone}`
          : phone ?? "";
      },
    },
    {
      title: ltlCustomersString.address1,
      dataIndex: "address1",
      isSorter: true,
      width: "10%",
    },
    {
      title: ltlCustomersString.address2,
      dataIndex: "address2",
      isSorter: true,
      width: "10%",
    },
    {
      title: ltlCustomersString.city,
      dataIndex: "city",
      isSorter: true,
      width: "9%",
    },
    {
      title: ltlCustomersString.state,
      dataIndex: "state",
      isSorter: true,
      width: "7%",
    },
    {
      title: ltlCustomersString.zip,
      dataIndex: "zip",
      isSorter: true,
      width: "8%",
    },
    {
      title: ltlCustomersString.hours,
      dataIndex: "workingHours",
      isSorter: true,
      width: "10%",
    },
    {
      title: ltlCustomersString.isDelayedDeparture,
      dataIndex: "deliveryApptRequired",
      isSorter: true,
      width: "15%",
      render: (isDelayedDeparture: any) => {
        return isDelayedDeparture ? "Yes" : "No";
      },
    },
    {
      title: ltlCustomersString.specialInstructions,
      dataIndex: "specialInstructions",
      isSorter: true,
      width: "15%",
      ellipsis: true,
      render: (text: string) =>
        text && text.length > 200 ? `${text.substring(0, 200)}...` : text || "",
    },
  ];

  const popOverIconStyle = {
    color: BASE_COLORS.curiousBlue,
    marginRight: 5,
  };

  const columnAction = {
    title: stringConfig.actions,
    dataIndex: "",
    key: "x",
    width: "7%",
    render: (item: any) => (
      <EditOutlined
        style={{ cursor: "pointer", ...popOverIconStyle }}
        onClick={() => handleEdit(item)}
      />
    ),
  };

  return (
    <ActivityWrapper>
      <InfoWrapper>
        <Breadcrumb
          items={[
            {
              title: <HomeOutlined />,
            },
            {
              title: <span style={{ cursor: "pointer" }}>Settings</span>,
              onClick: () => navigate("/settings"),
            },
            {
              title: <span>LTL Customers</span>,
            },
          ]}
        />
        <Tooltip
          title="View and manage customer information. This page provides an interface for adding and updating customers, including their default shipment details, which are used for TMS submissions."
          placement="bottomRight"
          overlayInnerStyle={{ maxWidth: 300 }}
        >
          <InfoCircleOutlined style={{ cursor: "pointer" }} />
        </Tooltip>
      </InfoWrapper>
      <Divider style={{ marginBottom: "10px", marginTop: "10px" }} />
      <ActivityLogWrapper>
        <Col span={24} style={{ marginTop: 15 }} className="tableRow">
          <TableHeader
            showModal={() => openAddUser("ADD")}
            placeHolder={stringConfig.searchPlaceholder}
            buttonText={ltlCustomersString.addCustomer}
            inputWidth={isMobile ? "100%" : 180}
            leftHeaderText={ltlCustomersString.customerLog}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            onServerSearch={handleSearch}
            searchLoading={loading}
            initialData={initialLtlCustomerList}
            columnsKey={columnsData.map(({ dataIndex }) => dataIndex)}
            // isShowButton={!hasViewAccess}
          />
          <DynamicTable
            columns={columnsData}
            columnActions={columnAction}
            data={ltlCustomerListData}
            loading={loading}
            onChange={handleTableChange}
            isPagination={true}
            pagination={pagination}
            sortField={sortField}
            sortOrder={sortOrder}
            totalCounts={pagination.total}
            initialData={initialLtlCustomerList}
            scrollXWidth={1350}
          />
          {isModalOpen.open && (
            // <AddActionModal
            //   title={
            //     isModalOpen.mode === "EDIT" ? "Edit Customer" : "Add Customer"
            //   }
            //   isModalOpen={isModalOpen}
            //   handleOk={handleOk}
            //   handleCancel={handleCancel}
            //   cancelText={stringConfig.cancel}
            //   saveText={stringConfig.submit}
            //   pending={loading}
            //   className="add-customer-modal"
            //   formName="customer"
            //   width="1000px"
            // >
              <Drawer
               title={
                isModalOpen.mode === "EDIT" ? "Edit Customer" : "Add Customer"
              }
              className="add-customer-modal"
              width={700}
              open={isModalOpen.open}
              destroyOnClose
              onClose={() => handleCancel()}
              >
              <EditLtlCustomers
                form={form}
                handleOk={handleOk}
                isEdit={isModalOpen.mode === "EDIT"}
                handleCancel={handleCancel}
                selectedRecord={selectedRecord}
                onClose={(isSuccess) => {
                  if (isSuccess) {
                    setSearchValue("");
                    setPagination((prev) => ({
                      ...prev,
                      current: TABLE_PAGINATION.DEFAULT_PAGE,
                    }));
                    dispatch(
                      fetchLTLCustomerListRequest(config.GetCustomersList, {
                        pageNumber: pagination.current,
                        pageSize: pagination.pageSize,
                        sortColumn: sortField,
                        sortOrder,
                        searchValue: "",
                      })
                    );
                  }
                  setSelectedRecord(null);
                  form.resetFields();
                  setIsModalOpen({
                    open: false,
                    mode: "",
                  });
                }}
              />
            {/* </AddActionModal> */}
            </Drawer>
          )}
        </Col>
      </ActivityLogWrapper>
    </ActivityWrapper>
  );
};
export default LtlCustomers;
